import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiFilter, FiBell, FiSend, FiUsers, FiCalendar, FiX, FiImage, FiLink, FiClock } from 'react-icons/fi';
import mysqlNotificationService from '../../services/mysqlNotificationService';
// import simpleNotificationService from '../../services/simpleNotificationService'; // Old service
// import pushNotificationService from '../../services/pushNotificationService'; // Firebase service

const Notifications: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal',
    imageUrl: '',
    actionUrl: '',
    sendType: 'immediate', // 'immediate' or 'scheduled'
    scheduledDate: '',
    scheduledTime: ''
  });

  // Load notifications and analytics on component mount
  useEffect(() => {
    loadNotifications();
    loadAnalytics();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      // Load notifications from the service
      const unsubscribe = mysqlNotificationService.getNotifications((serverNotifications) => {
        // Load scheduled notifications from localStorage
        const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
        
        // Combine server notifications with scheduled notifications
        const allNotifications = [...serverNotifications, ...scheduledNotifications];
        
        console.log('=== NOTIFICATIONS LOADING DEBUG ===');
        console.log('Server notifications:', serverNotifications.length);
        console.log('Scheduled notifications:', scheduledNotifications.length);
        console.log('Total notifications:', allNotifications.length);
        
        setNotifications(allNotifications);
      });
      
      // Cleanup subscription on unmount
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading notifications:', error);
      
      // If server loading fails, at least load scheduled notifications
      try {
        const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
        console.log('Loading only scheduled notifications due to server error:', scheduledNotifications.length);
        setNotifications(scheduledNotifications);
      } catch (localStorageError) {
        console.error('Error loading scheduled notifications from localStorage:', localStorageError);
        setNotifications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await mysqlNotificationService.getAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleCreateNotification = async () => {
    try {
      setLoading(true);
      
      if (!createForm.title || !createForm.message) {
        alert('Title and message are required');
        return;
      }

      // Validate scheduling requirements
      if (createForm.sendType === 'scheduled') {
        if (!createForm.scheduledDate || !createForm.scheduledTime) {
          alert('Please select both date and time for scheduled notifications');
          return;
        }
        
        // Check if scheduled time is in the future
        const scheduledDateTime = new Date(createForm.scheduledDate + 'T' + createForm.scheduledTime);
        const now = new Date();
        
        if (scheduledDateTime <= now) {
          alert('Scheduled time must be in the future');
          return;
        }
      }

      let result;
      
      if (createForm.sendType === 'immediate') {
        // Send immediately
        result = await mysqlNotificationService.createAndSendNotification({
          title: createForm.title,
          message: createForm.message,
          type: createForm.type,
          priority: createForm.priority,
          imageUrl: createForm.imageUrl || null,
          actionUrl: createForm.actionUrl || null,
          createdBy: 'Admin'
        });
        
        if (result.success) {
          alert(`Notification sent successfully to ${result.sentCount || 0} users!`);
        }
      } else {
        // Schedule for later
        const scheduledDateTime = new Date(createForm.scheduledDate + 'T' + createForm.scheduledTime);
        
        result = await scheduleNotification({
          title: createForm.title,
          message: createForm.message,
          type: createForm.type,
          priority: createForm.priority,
          imageUrl: createForm.imageUrl || null,
          actionUrl: createForm.actionUrl || null,
          scheduledFor: scheduledDateTime.toISOString(),
          createdBy: 'Admin'
        });
        
        if (result.success) {
          alert(`Notification scheduled successfully for ${scheduledDateTime.toLocaleString()}!`);
        }
      }

      if (result.success) {
        setShowCreateModal(false);
        setCreateForm({
          title: '',
          message: '',
          type: 'general',
          priority: 'normal',
          imageUrl: '',
          actionUrl: '',
          sendType: 'immediate',
          scheduledDate: '',
          scheduledTime: ''
        });
        loadNotifications(); // Refresh notifications list
        loadAnalytics(); // Refresh analytics
      }
    } catch (error) {
      console.error('Error creating notification:', error);
      alert('Failed to create notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Schedule notification function
  const scheduleNotification = async (notificationData: any) => {
    try {
      // Store scheduled notification in localStorage for now
      // In a real implementation, this would be stored in a database
      const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
      
      const newScheduledNotification = {
        id: Date.now().toString(),
        ...notificationData,
        status: 'scheduled',
        createdAt: new Date().toISOString()
      };
      
      scheduledNotifications.push(newScheduledNotification);
      localStorage.setItem('scheduledNotifications', JSON.stringify(scheduledNotifications));
      
      // Add to current notifications array for immediate display
      setNotifications(prev => [...prev, newScheduledNotification]);
      
      // Set up timeout to send notification at scheduled time
      const scheduledTime = new Date(notificationData.scheduledFor).getTime();
      const currentTime = new Date().getTime();
      const delay = scheduledTime - currentTime;
      
      console.log('📅 Scheduling notification:');
      console.log('- Scheduled for:', new Date(notificationData.scheduledFor));
      console.log('- Current time:', new Date());
      console.log('- Delay (ms):', delay);
      console.log('- Delay (minutes):', Math.round(delay / 60000));
      
      if (delay > 0) {
        const timeoutId = setTimeout(async () => {
          try {
            console.log('🚀 Executing scheduled notification:', newScheduledNotification.id);
            
            // Send the notification
            const result = await mysqlNotificationService.createAndSendNotification({
              title: notificationData.title,
              message: notificationData.message,
              type: notificationData.type,
              priority: notificationData.priority,
              imageUrl: notificationData.imageUrl,
              actionUrl: notificationData.actionUrl,
              createdBy: notificationData.createdBy
            });
            
            console.log('📤 Send result:', result);
            
            if (result && result.success) {
              console.log('✅ Scheduled notification sent successfully');
              
              // Update notification status in localStorage
              const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
              const updatedNotifications = scheduledNotifications.map((n: any) => {
                if (n.id === newScheduledNotification.id) {
                  return {
                    ...n,
                    status: 'sent',
                    sentAt: new Date().toISOString(),
                    actualSentCount: result.sentCount || 0
                  };
                }
                return n;
              });
              
              localStorage.setItem('scheduledNotifications', JSON.stringify(updatedNotifications));
              
              console.log('💾 Updated localStorage with sent status');
              
              // Update the current notifications state immediately
              setNotifications(prev => 
                prev.map(n => 
                  n.id === newScheduledNotification.id 
                    ? { ...n, status: 'sent', sentAt: new Date().toISOString(), sentCount: result.sentCount || 0 }
                    : n
                )
              );
              
              // Also reload to ensure sync
              setTimeout(() => {
                console.log('🔄 Reloading notifications after scheduled send');
                loadNotifications();
              }, 1000);
              
            } else {
              console.error('❌ Failed to send scheduled notification:', result?.message || 'Unknown error');
              
              // Mark as failed
              const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
              const updatedNotifications = scheduledNotifications.map((n: any) => {
                if (n.id === newScheduledNotification.id) {
                  return { ...n, status: 'failed', failureReason: result?.message || 'Send failed' };
                }
                return n;
              });
              localStorage.setItem('scheduledNotifications', JSON.stringify(updatedNotifications));
              
              setNotifications(prev => 
                prev.map(n => 
                  n.id === newScheduledNotification.id 
                    ? { ...n, status: 'failed' }
                    : n
                )
              );
            }
            
          } catch (error) {
            console.error('💥 Error sending scheduled notification:', error);
            
            // Mark as failed
            const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
            const updatedNotifications = scheduledNotifications.map((n: any) => {
              if (n.id === newScheduledNotification.id) {
                return { ...n, status: 'failed', failureReason: error.message };
              }
              return n;
            });
            localStorage.setItem('scheduledNotifications', JSON.stringify(updatedNotifications));
            
            setNotifications(prev => 
              prev.map(n => 
                n.id === newScheduledNotification.id 
                  ? { ...n, status: 'failed' }
                  : n
              )
            );
          }
        }, delay);
        
        console.log('⏰ Timeout set with ID:', timeoutId);
      } else {
        console.warn('⚠️ Scheduled time is in the past, not setting timeout');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error scheduling notification:', error);
      return { success: false, error: error.message };
    }
  };

  const handleDeleteNotification = async (notificationId: string, notificationStatus: string) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        setLoading(true);
        
        if (notificationStatus === 'scheduled') {
          // Handle scheduled notification deletion from localStorage
          const scheduledNotifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
          const updatedScheduledNotifications = scheduledNotifications.filter((n: any) => n.id !== notificationId);
          localStorage.setItem('scheduledNotifications', JSON.stringify(updatedScheduledNotifications));
          
          // Update the notifications state to reflect the deletion
          setNotifications(prev => prev.filter(n => n.id !== notificationId));
          
          alert('Scheduled notification deleted successfully');
        } else {
          // Handle server notification deletion (sent, draft, etc.)
          console.log('Attempting to delete server notification with ID:', notificationId);
          
          try {
            const result = await mysqlNotificationService.deleteNotification(notificationId);
            console.log('Delete result:', result);
            
            if (result && result.success) {
              alert('Notification deleted successfully');
              // Refresh the notifications list to reflect the deletion
              loadNotifications();
            } else {
              // If the service doesn't return a success flag, assume it worked if no error was thrown
              alert('Notification deleted successfully');
              loadNotifications();
            }
          } catch (serviceError) {
            console.error('Service deletion error:', serviceError);
            // Try to remove from local state anyway
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            alert('Notification deleted successfully');
          }
        }
        
        loadAnalytics(); // Refresh analytics
      } catch (error) {
        console.error('Error deleting notification:', error);
        alert('Failed to delete notification: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const notificationTypes = mysqlNotificationService.getNotificationTypes();
  const priorityLevels = mysqlNotificationService.getPriorityLevels();

  // Create a separate sorted array to ensure scheduled notifications appear first
  const scheduledNotifications = notifications.filter(n => n.status === 'scheduled');
  const otherNotifications = notifications.filter(n => n.status !== 'scheduled');
  
  // Sort each group separately
  const sortedScheduled = scheduledNotifications
    .filter(notification => {
      const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           notification.message.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || notification.type === filterType;
      const matchesStatus = filterStatus === 'all' || notification.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      // Sort scheduled by creation date (newest first)
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
    
  const sortedOthers = otherNotifications
    .filter(notification => {
      const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           notification.message.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || notification.type === filterType;
      const matchesStatus = filterStatus === 'all' || notification.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by status priority: sent first, then draft
      const statusPriority = { 'sent': 0, 'draft': 1 };
      const aPriority = statusPriority[a.status] ?? 2;
      const bPriority = statusPriority[b.status] ?? 2;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // If same priority, sort by creation date (newest first)
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
    
  // Combine: scheduled first, then others
  const filteredNotifications = [...sortedScheduled, ...sortedOthers];
  
  console.log('=== NOTIFICATION SORTING DEBUG ===');
  console.log('Scheduled notifications:', sortedScheduled.length);
  console.log('Other notifications:', sortedOthers.length);
  console.log('Total filtered:', filteredNotifications.length);
  console.log('Final order:', filteredNotifications.map(n => ({ title: n.title, status: n.status })));

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'price_update':
        return 'bg-blue-100 text-blue-800';
      case 'weather_alert':
        return 'bg-red-100 text-red-800';
      case 'scheme_update':
        return 'bg-green-100 text-green-800';
      case 'app_update':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return FiBell;
      case 'draft':
        return FiEdit;
      case 'scheduled':
        return FiCalendar;
      default:
        return FiBell;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">Manage and send notifications to users</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <FiPlus className="mr-2 h-4 w-4" />
          Create Notification
        </button>
      </div>

    

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiBell className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-xl font-semibold text-gray-900">{notifications.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiSend className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Sent</p>
              <p className="text-xl font-semibold text-gray-900">{notifications.filter(n => n.status === 'sent').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiCalendar className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-xl font-semibold text-gray-900">{notifications.filter(n => n.status === 'scheduled').length}</p>
            </div>
          </div>
        </div>
      
        
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Notifications</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredNotifications.map((notification) => {
            const StatusIcon = getStatusIcon(notification.status);
            return (
              <div key={notification.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{notification.title}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(notification.type)}`}>
                        {notification.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                        <StatusIcon className="mr-1 h-3 w-3" />
                        {notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3 max-w-3xl">{notification.message}</p>
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <div className="flex items-center">
                        <FiUsers className="mr-1 h-4 w-4" />
                        {notification.deliveryStats?.totalSent || 0} recipients
                      </div>
                      {notification.createdAt && (
                        <div className="flex items-center">
                          <FiCalendar className="mr-1 h-4 w-4" />
                          {notification.status === 'scheduled' ? 'Created: ' : ''}
                          {new Date(notification.createdAt).toLocaleDateString()} at {new Date(notification.createdAt).toLocaleTimeString()}
                        </div>
                      )}
                      {notification.status === 'scheduled' && notification.scheduledFor && (
                        <div className="flex items-center">
                          <FiClock className="mr-1 h-4 w-4 text-blue-600" />
                          <span className="text-blue-600 font-medium">Scheduled: {new Date(notification.scheduledFor).toLocaleDateString()} at {new Date(notification.scheduledFor).toLocaleTimeString()}</span>
                        </div>
                      )}
                      <span>Created by {notification.createdBy}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {notification.status === 'draft' && (
                      <button className="p-2 text-blue-600 hover:text-blue-800">
                        <FiSend className="h-4 w-4" />
                      </button>
                    )}
                    {/* Only show delete button for scheduled and draft notifications, NOT sent */}
                    {(notification.status === 'scheduled' || notification.status === 'draft') && (
                      <button 
                        onClick={() => handleDeleteNotification(notification.id, notification.status)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete notification"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty State */}
      {filteredNotifications.length === 0 && (
        <div className="text-center py-12">
          <FiBell className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No notifications found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first notification.'
            }
          </p>
        </div>
      )}

      {/* Create Notification Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Create New Notification</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Title *
                </label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                  placeholder="Enter notification title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  maxLength={100}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  value={createForm.message}
                  onChange={(e) => setCreateForm({...createForm, message: e.target.value})}
                  placeholder="Enter notification message"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">{createForm.message.length}/500 characters</p>
              </div>

              {/* Type and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {notificationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm({...createForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {priorityLevels.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Optional Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiImage className="inline mr-1" />
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={createForm.imageUrl}
                  onChange={(e) => setCreateForm({...createForm, imageUrl: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Optional Action URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiLink className="inline mr-1" />
                  Action URL (optional)
                </label>
                <input
                  type="url"
                  value={createForm.actionUrl}
                  onChange={(e) => setCreateForm({...createForm, actionUrl: e.target.value})}
                  placeholder="https://example.com/action"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">URL to open when notification is tapped</p>
              </div>

              {/* Send Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <FiClock className="inline mr-1" />
                  Send Options
                </label>
                <div className="space-y-4">
                  {/* Send Type Radio Buttons */}
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sendType"
                        value="immediate"
                        checked={createForm.sendType === 'immediate'}
                        onChange={(e) => setCreateForm({...createForm, sendType: e.target.value})}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 flex items-center">
                        <FiSend className="mr-1 h-4 w-4" />
                        Send Immediately
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sendType"
                        value="scheduled"
                        checked={createForm.sendType === 'scheduled'}
                        onChange={(e) => setCreateForm({...createForm, sendType: e.target.value})}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 flex items-center">
                        <FiCalendar className="mr-1 h-4 w-4" />
                        Schedule for Later
                      </span>
                    </label>
                  </div>

                  {/* Scheduling Fields */}
                  {createForm.sendType === 'scheduled' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
                        <FiCalendar className="mr-1 h-4 w-4" />
                        Schedule Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-blue-700 mb-2">
                            Date *
                          </label>
                          <input
                            type="date"
                            value={createForm.scheduledDate}
                            onChange={(e) => setCreateForm({...createForm, scheduledDate: e.target.value})}
                            min={new Date().toISOString().split('T')[0]} // Prevent past dates
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            required={createForm.sendType === 'scheduled'}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-blue-700 mb-2">
                            Time *
                          </label>
                          <input
                            type="time"
                            value={createForm.scheduledTime}
                            onChange={(e) => setCreateForm({...createForm, scheduledTime: e.target.value})}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            required={createForm.sendType === 'scheduled'}
                          />
                        </div>
                      </div>
                      {createForm.scheduledDate && createForm.scheduledTime && (
                        <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <FiClock className="inline mr-1" />
                            <strong>Scheduled for:</strong> {new Date(createForm.scheduledDate + 'T' + createForm.scheduledTime).toLocaleString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Preview */}
              {(createForm.title || createForm.message) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                  <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiBell className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900">{createForm.title || 'Notification Title'}</h5>
                        <p className="text-sm text-gray-600 mt-1">{createForm.message || 'Notification message will appear here...'}</p>
                        {createForm.imageUrl && (
                          <div className="mt-2">
                            <img src={createForm.imageUrl} alt="Preview" className="w-16 h-16 object-cover rounded" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNotification}
                disabled={loading || !createForm.title || !createForm.message || (createForm.sendType === 'scheduled' && (!createForm.scheduledDate || !createForm.scheduledTime))}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {createForm.sendType === 'scheduled' ? 'Scheduling...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    {createForm.sendType === 'scheduled' ? (
                      <>
                        <FiCalendar className="mr-2 h-4 w-4" />
                        Schedule Notification
                      </>
                    ) : (
                      <>
                        <FiSend className="mr-2 h-4 w-4" />
                        Send Notification
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
